import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import { CustomBreadCrumb } from '@/components/customBreadCrumb';
import './style.scss';
import {
  CustomInput,
  CustomPagination,
  CustomTable,
  PaginationText,
} from '@/components';
import { Edit, Trash } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PublisherType } from '@/DTO';
import { PublisherApi } from '@/api';
import { useAppSelector } from '@/redux-config/hooks';
import { selectUser } from '@/redux-config/entities/user';
import { InsideLoading } from '@/components/loading/inside';
import { selectLanguage } from '@/redux-config/entities/language';
import { ConvertDate, ConvertNumber } from '@/utils';
import { SearchComponent } from '@/components/search';
import { CustomModal } from '@/components/custom-modal';
import { useAppPagination } from '@/Router/hook';

export const PublisherList: React.FC<{}> = () => {
  const { t } = useTranslation();

  const crumbs = [
    { name: t('Dashboard'), href: '/' },
    { name: t('Category'), href: '#' },
    { name: t('List'), href: '#' },
  ];

  const navigate = useNavigate();
  const lang = useAppSelector(selectLanguage);
  const user = useAppSelector(selectUser);
  const [loading, setLoading] = useState<boolean>(true);
  const [pagination, setPagination] = useState(useAppPagination());

  const [data, setData] = useState<PublisherType[]>([]);
  const [searchData, setSearchData] = useState<string>();

  const [showModal, setShowModal] = useState<boolean>(false);
  const [modalDataID, setModalDataID] = useState<string>();
  const [modalDataText, setModalDataText] = useState<string>('');

  const [showModalNew, setShowModalNew] = useState<boolean>(false);
  const [modalNewDataText, setModalNewDataText] = useState<string>('');

  const handleGetData = (page: number) => {
    setLoading(true);
    PublisherApi.GetPublisherList(
      user,
      { ...pagination, currentPage: page },
      searchData,
    )
      .then((data) => {
        setData(data.data);
        setPagination(data.pagination);
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
      });
  };

  useEffect(() => {
    handleGetData(pagination.currentPage);
  }, []);

  const handleCreate = () => {
    PublisherApi.CreatePublisher(user, modalNewDataText)
      .then(() => {
        setShowModalNew(false);
        setModalNewDataText("")
        handleGetData(pagination.currentPage);
      })
      .catch((err) => {
        setShowModalNew(false);
      });
  };

  const handleEdit = () => {
    PublisherApi.EditPublisher(user, modalDataID + '', modalDataText)
      .then(() => {
        setShowModal(false);
        setModalDataText("")
        handleGetData(pagination.currentPage);
      })
      .catch((err) => {
        setShowModal(false);
      });
  };

  const handleDelete = (id: string) => {
    PublisherApi.RemovePublisher(user, id + '')
      .then(() => {
        setShowModal(false);
        handleGetData(pagination.currentPage);
      })
      .catch((err) => {
        setShowModal(false);
      });
  };

  return (
    <>
      <Helmet>
        <title>MahdiBookShop | Publisher List</title>
        <meta name="description" content="Basic example" />
      </Helmet>
      <div className="d-flex flex-column">
        <CustomBreadCrumb breadCrumbList={crumbs} />
        <div className="w-100 p-2">
          <div className="rounded-3 py-2 bg-custom-white h-100 d-flex flex-column justify-content-between">
            <div
              className={
                'd-flex flex-row gap-2 align-items-center justify-content-between p-3'
              }
            >
              <div className={'d-flex flex-row align-items-center gap-2 '}>
                <h5 className={'pt-2'}>{t('Publishers')}</h5>
                <CustomInput
                  type={'text_number'}
                  name={'search-for-list'}
                  placeholder={t('search') + ''}
                  className={'custom-input border-reg rounded-5'}
                  onChange={setSearchData}
                  value={searchData}
                />
                <SearchComponent
                  fetchDataApi={() => {
                    handleGetData(1);
                  }}
                  name={searchData}
                />
              </div>
              <button
                onClick={() => setShowModalNew(true)}
                className="prm-btn rounded-3 mx-1 text-nowrap"
              >
                <span>{t('New')}</span>
              </button>
            </div>
            {loading ? (
              <InsideLoading />
            ) : (
              <>
                <CustomTable
                  header={[
                    <span>{t('No')}</span>,
                    <span>{t('Name')}</span>,
                    <span>{t('Date')}</span>,
                    <span>{t('Options')}</span>,
                  ]}
                  items={[
                    ...data.map((item, key) => [
                      <span>{ConvertNumber(key + 1 + (pagination.currentPage - 1) * pagination.perPage + "", lang)} </span>,
                      <span>{item.name}</span>,
                      <span>{ConvertDate(item.created_at + '', lang)}</span>,
                      <div
                        className={
                          'd-flex flex-row gap-1 justify-content-center'
                        }
                      >
                        <button
                          className="btn p-1 align-baseline rounded-circle bg-custom-light"
                          onClick={() => {
                            handleDelete(item.id);
                          }}
                        >
                          <Trash />
                        </button>
                        <button
                          className="btn p-1 align-baseline rounded-circle bg-custom-light"
                          onClick={() => {
                            setShowModal(true);
                            setModalDataID(item.id);
                            setModalDataText(item.name);
                          }}
                        >
                          <Edit />
                        </button>
                      </div>,
                    ]),
                  ]}
                />
                <div
                  className={
                    'd-flex flex-row p-3 align-items-center justify-content-between'
                  }
                >
                  <span>
                    <PaginationText pagination={pagination} />
                  </span>
                  <CustomPagination
                    currentPage={pagination.currentPage}
                    perPage={pagination.perPage}
                    changePage={(data: string) => {
                      navigate(`/publisher?current_page=${data}`);
                      setPagination({ ...pagination, currentPage: +data });
                      handleGetData(+data);
                    }}
                    total={pagination.total}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <CustomModal
        showModal={showModal}
        setShowModal={setShowModal}
        title={modalDataText}
      >
        <div className={'d-flex flex-column gap-3'}>
          <label className={'d-flex flex-column'}>
            <span>{t('Name')}</span>
            <CustomInput
              type={'text_number'}
              name={'name of Publisher'}
              className={'custom-input border-reg'}
              value={modalDataText}
              onChange={setModalDataText}
            />
          </label>

          <button
            className={'prm-btn rounded-3 align-self-start'}
            onClick={() => {
              if (modalDataID) {
                handleEdit();
              }
            }}
          >
            {t('submit')}
          </button>
        </div>
      </CustomModal>

      <CustomModal
        showModal={showModalNew}
        setShowModal={setShowModalNew}
        title={t('New') + ''}
      >
        <div className={'d-flex flex-column gap-3'}>
          <label className={'d-flex flex-column'}>
            <span>{t('Name')}</span>
            <CustomInput
              type={'text_number'}
              name={'name of Publisher'}
              className={'custom-input border-reg'}
              value={modalNewDataText}
              onChange={setModalNewDataText}
            />
          </label>
          <button
            className={'prm-btn rounded-3 align-self-start'}
            onClick={() => {
              handleCreate();
            }}
          >
            {t('submit')}
          </button>
        </div>
      </CustomModal>
    </>
  );
};
